// src/components/MailingListModal.js
// Modal popup per iscrizione mailing list — GDPR compliant
import React, { useState, useEffect, useContext } from "react";
import { LangContext } from "../lang/LangProvider";
import { useAnalytics } from "../hooks/useAnalytics";
import "./MailingListModal.css";

export default function MailingListModal({ isOpen, onClose, context = "generic" }) {
  const { lang } = useContext(LangContext);
  const { trackModalOpen, trackFormSubmit, trackFormValidationError } = useAnalytics();
  const [form, setForm] = useState({ name: "", email: "", gdpr: false });
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Chiudi con ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Traccia apertura modale
  useEffect(() => {
    if (isOpen) {
      trackModalOpen("mailing_list", context, lang);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Blocca scroll body quando aperto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  const texts = {
    it: {
      title: "Iscriviti agli aggiornamenti",
      subtitle: "Aggiornamenti sui nuovi articoli, serie e strumenti del sito. Al momento non vengono inviate email periodiche: se in futuro partiranno, riceverai solo aggiornamenti sui contenuti, nessuna comunicazione promozionale di servizi.",
      namePlaceholder: "Il tuo nome",
      emailPlaceholder: "la.tua@email.com",
      nameLabel: "Nome *",
      emailLabel: "Email *",
      gdprText: "Ho letto l'",
      gdprLink: "informativa privacy",
      gdprSuffix: " e chiedo di ricevere via email aggiornamenti sui nuovi contenuti del sito. Posso revocare la richiesta in qualsiasi momento. *",
      submit: "Iscrivimi",
      successTitle: "✅ Grazie!",
      successText: "Il tuo client email si è aperto con la richiesta di iscrizione. Invia il messaggio per completare la registrazione.",
      close: "Chiudi",
      errorFields: "Compila tutti i campi obbligatori.",
      errorGdpr: "Conferma la richiesta per continuare.",
      note: "* Si apre il tuo programma di posta con un messaggio per alphaframeanalytics.it@gmail.com: il sito non memorizza i dati inseriti.",
    },
    en: {
      title: "Subscribe to updates",
      subtitle: "Updates on new articles, series and tools on the site. No periodic emails are sent at the moment: if they start in the future, you will receive only content updates, no promotional communications about services.",
      namePlaceholder: "Your name",
      emailPlaceholder: "your@email.com",
      nameLabel: "Name *",
      emailLabel: "Email *",
      gdprText: "I have read the ",
      gdprLink: "privacy notice",
      gdprSuffix: " and ask to receive email updates on new website content. I can withdraw this request at any time. *",
      submit: "Subscribe",
      successTitle: "✅ Thank you!",
      successText: "Your email client has opened with the subscription request. Send the message to complete your registration.",
      close: "Close",
      errorFields: "Please fill in all required fields.",
      errorGdpr: "Please confirm the request to continue.",
      note: "* Your email program opens with a message to alphaframeanalytics.it@gmail.com: the website does not store the data entered.",
    },
  };

  const t = lang === "en" ? texts.en : texts.it;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setError(t.errorFields);
      trackFormValidationError("mailing_list", "missing_fields");
      return;
    }
    if (!form.gdpr) {
      setError(t.errorGdpr);
      trackFormValidationError("mailing_list", "missing_gdpr");
      return;
    }
    setError("");
    trackFormSubmit("mailing_list", true, null);

    const subject = lang === "en"
      ? "AlphaFrame — Mailing list subscription"
      : "AlphaFrame — Iscrizione mailing list";
    const body = lang === "en"
      ? `Hi,\n\nI'd like to receive updates on new AlphaFrame Analytics content.\n\nName: ${form.name}\nEmail: ${form.email}\n\n[Request for content updates, privacy notice read — ${new Date().toISOString()}]`
      : `Ciao,\n\nVorrei ricevere gli aggiornamenti sui nuovi contenuti di AlphaFrame Analytics.\n\nNome: ${form.name}\nEmail: ${form.email}\n\n[Richiesta di aggiornamenti sui contenuti, informativa letta — ${new Date().toISOString()}]`;

    window.location.href = `mailto:alphaframeanalytics.it@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  const handleClose = () => {
    setForm({ name: "", email: "", gdpr: false });
    setError("");
    setSubmitted(false);
    onClose();
  };

  return (
    <div className="ml-overlay" role="dialog" aria-modal="true" aria-labelledby="ml-title" onClick={handleClose}>
      <div className="ml-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ml-close" onClick={handleClose} aria-label="Chiudi">×</button>

        {submitted ? (
          <div className="ml-success">
            <h2>{t.successTitle}</h2>
            <p>{t.successText}</p>
            <button className="ml-btn-primary" onClick={handleClose}>{t.close}</button>
          </div>
        ) : (
          <>
            <h2 id="ml-title" className="ml-title">{t.title}</h2>
            <p className="ml-subtitle">{t.subtitle}</p>

            <form className="ml-form" onSubmit={handleSubmit} noValidate>
              <label htmlFor="ml-name">
                {t.nameLabel}
                <input
                  id="ml-name" name="name" type="text" required
                  value={form.name} onChange={handleChange}
                  placeholder={t.namePlaceholder}
                  autoComplete="name"
                />
              </label>

              <label htmlFor="ml-email">
                {t.emailLabel}
                <input
                  id="ml-email" name="email" type="email" required
                  value={form.email} onChange={handleChange}
                  placeholder={t.emailPlaceholder}
                  autoComplete="email"
                />
              </label>

              <label className="ml-gdpr-label">
                <input type="checkbox" name="gdpr" checked={form.gdpr} onChange={handleChange} />
                <span>
                  {t.gdprText}
                  <a href={`/${lang}/privacy`} target="_blank" rel="noopener noreferrer">{t.gdprLink}</a>
                  {t.gdprSuffix}
                </span>
              </label>

              {error && <p className="ml-error">{error}</p>}

              <button type="submit" className="ml-btn-primary">{t.submit}</button>

              <p className="ml-note"><small>{t.note}</small></p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
